// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import partition from 'lodash/partition';
import max from 'lodash/max';
import cloneDeep from 'lodash/cloneDeep';
import min from 'lodash/min';
import moment from 'moment';
import type {Moment} from 'moment';

import {convertBaseDateToCalendarDate} from 'common/widgets/date-range-picker/models/utils';
import {encodeRison} from 'common/report/util/url';
import {
  CohortGroupEntry,
  FiltersOperator,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {createDefaultCohortMetricEntry, getEntryFromLiteral} from 'common/report/util/cohorts/transformations/entries';
import {cohortFilterIncludesId, isLookupPropertyFilter} from 'common/widgets/property-filter-menu/models/utils';
import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {isBehavioralCustomProperty} from 'common/widgets/custom-property-modal/util';
import {
  getBehaviorEntryTrackingProps,
  transformBehavioralFilterToBehavioralEntry,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {generateFilterTrackingProps} from 'common/query-builder/query-block/filter-menu/util';
import {reporter} from 'common/report/error-reporting';
import {createCohortCommonPropertyFilter} from 'common/report/util/cohorts/transformations/filters';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {NthTimeOperator} from 'common/widgets/property-filter-menu/models/types';
import {isExperimentSegment} from 'common/query-builder/query-block/segment-menu/types';

import {parseDate} from '../util';
import {
  getLiteralTrees,
  getLogicalOperators,
  getSingleClauseLiteralTrees,
  getSingleClauseLogicalOperators,
  isCohortLiteralTree,
  isEventLiteralTree,
  onlyLeftAssociatedAndOperators,
} from '../util/expression-tree';
import {PeopleLiteral} from './builder/people-literal';
import {LITERAL_NAMES} from './constants';
import {OperatorNode} from './expression/operator-node';
import {Serializable, SerializedCohort, User} from './types/serialized-cohort-types';
import {Trackable} from './types/tracking';
import {Behavior, BehaviorsDictionary} from './behaviors/behavior';
import {EventLiteral} from './builder/event-literal';
import {StagedCohort} from './builder/staged-cohort';
import {CohortLiteral} from './builder/cohort-literal';

interface CohortOptions {
  behaviors?: BehaviorsDictionary;
  count?: number;
  createdBy?: User;
  activeIntegrations?: string[];
  dependentCohortIds?: number[];
  description?: string;
  id?: number;
  unsavedId?: string;
  isNew?: boolean;
  lastEdited?: Moment;
  lastQueried?: Moment;
  reportName?: string;
  name?: string;
  selector?: OperatorNode;
  dataGroupId?: string;

  // new cohorts format
  groups?: CohortGroupEntry[];

  // verification
  verified?: boolean;
  verifiedBy?: User | null;
  lastVerified?: Moment | null;

  // permissions
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;
}

export class Cohort implements Serializable, Trackable {
  public readonly id: number = null;
  public unsavedId: string = null;
  public isNew = false;
  public name = ``;
  public activeIntegrations: string[] = [];
  public declare description: string;
  public declare createdBy: User;
  public declare count: number;
  public declare lastEdited: Moment;
  public declare lastQueried: Moment;
  public declare reportName: null;
  public selector: OperatorNode = null;
  public behaviors: BehaviorsDictionary = {};
  public dependentCohortIds: number[] = [];
  public declare dataGroupId?: string;

  // verification
  public verified: boolean = false;
  public declare verifiedBy?: User | null;
  public declare lastVerified?: Moment | null;

  // permissions
  public declare can_view?: boolean;
  public declare can_update_basic?: boolean;
  public declare can_share?: boolean;
  public declare is_superadmin?: boolean;
  public declare allow_staff_override?: boolean;

  // new cohorts format
  private _groups: CohortGroupEntry[] = [];

  public static parseCohort(serializedCohort: SerializedCohort): Cohort {
    /* eslint-disable @typescript-eslint/naming-convention */
    const {
      id,
      unsavedId,
      name,
      description,
      active_integrations,
      count,
      created_by,
      selector,
      data_group_id,
      last_edited,
      last_queried,
      referenced_by,

      // new cohorts format
      groups,

      // verification
      verified,
      verified_by,
      last_verified,

      // permissions
      can_view,
      can_update_basic,
      can_share,
      is_superadmin,
      allow_staff_override,
    } = serializedCohort;
    /* eslint-enable */
    const behaviors = serializedCohort.behaviors || {};
    return new Cohort({
      activeIntegrations: active_integrations,
      behaviors: Behavior.parseBehaviors(behaviors),
      count,
      createdBy: {
        email: created_by ? created_by.email : null,
        name: created_by ? created_by.name : null,
        id: created_by ? created_by.id : null,
      },
      dependentCohortIds: referenced_by || [],
      description,
      id: id ?? null,
      unsavedId: unsavedId ?? null,
      lastEdited: moment(last_edited),
      lastQueried: last_queried ? moment(last_queried) : null,
      name,
      reportName: Behavior.getReportName(behaviors),
      selector: OperatorNode.parseExpression(selector) as OperatorNode,
      ...(data_group_id ? {dataGroupId: data_group_id.toString()} : {}),

      // new cohorts format
      groups: groups || [],
      /** @todo: [pablo] populate groups for report based cohort **/

      // verification
      verified,
      verifiedBy: verified_by || null,
      lastVerified: last_verified ? moment(last_verified) : null,

      // permissions
      can_view,
      can_update_basic,
      can_share,
      is_superadmin,
      allow_staff_override,
    });
  }

  public static createCohortFromStaged(stagedCohort: StagedCohort): Cohort {
    const {name, description, id, dataGroupId, createdBy} = stagedCohort;
    const behaviors = stagedCohort.eventLiterals.reduce(
      (dict, literal) => ({...dict, ...literal.behaviorsDictionary}),
      {},
    );
    return new Cohort({
      behaviors,
      description,
      id,
      unsavedId: stagedCohort.unsavedId,
      name,
      // a staged cohort does not define groups within the cohorts format
      groups: null,
      selector: stagedCohort.getExpressionTree(),
      dataGroupId,
      createdBy,
    });
  }

  public static createUsersInCohort({
    cohortId,
    dataGroupId,
    serializedCohort,
    extraFilters,
  }: {
    cohortId: number;
    dataGroupId?: string;
    serializedCohort?: SerializedCohort;
    extraFilters?: PropertyFilter[];
  }): Cohort {
    const filterValue = Number.isInteger(cohortId) ? {cohortId} : {rawCohort: serializedCohort};
    return new Cohort({
      groups: [
        {
          ...createDefaultCohortMetricEntry(),
          filters: [createCohortCommonPropertyFilter(filterValue), ...(extraFilters || [])],
        },
      ],
      behaviors: {},
      selector: null,
      name: null,
      ...(dataGroupId ? {dataGroupId} : {}),
    });
  }

  public static getExploreHashForUsersInCohort({
    cohortId,
    dataGroupId,
    cohortName,
    serializedCohort,
    extraFilters,
  }: {
    cohortId: number;
    dataGroupId?: string;
    cohortName: string;
    serializedCohort?: SerializedCohort;
    extraFilters?: PropertyFilter[];
  }) {
    const params = {
      sort_property: `$last_seen`,
      sort_order: `descending`,
      sort_property_type: `datetime`,
      include_all_users: true,
      cohort_name: encodeURIComponent(`Viewing "${cohortName}"`),
      selected: [],
      ...(dataGroupId ? {data_group_id: dataGroupId} : {}),
    };

    params[`filter_by_cohort`] = Cohort.createUsersInCohort({
      cohortId,
      serializedCohort,
      ...(dataGroupId ? {dataGroupId} : {}),
      extraFilters,
    }).serialize();

    return `#list/${encodeRison(params)}`;
  }

  constructor(options: CohortOptions = null) {
    Object.assign(this, options);

    if (isEmpty(options)) {
      this.groups = [createDefaultCohortMetricEntry()];
    } else if (this.selector && this.behaviors && Array.isArray(options.groups) && options.groups.length === 0) {
      // populate groups from the old format SerializedCohort
      try {
        const stagedCohort = this.toStagedCohort();
        this.groups = stagedCohort.literals
          .map((literal, literalIndex) =>
            getEntryFromLiteral(cloneDeep(literal), {
              literalIndex,
              dataGroupId: this.dataGroupId,
              rawCohort: this.serialize(),
              cohortId: this.id,
              groupingOperators: stagedCohort.operators,
            }),
          )
          .flat();
      } catch (error) {
        this.groups = [];
        reporter.warn(`Failed to convert query from Cohorts AST to common QB format`, {
          error,
          cohortId: options.id,
          behaviors: options.behaviors,
          selector: options.selector,
        });
      }
    }
  }

  get groups() {
    return this._groups;
  }

  /** performs sanitization on groups */
  set groups(groups) {
    this._groups = Array.isArray(groups)
      ? groups.map((group, index) => ({
          ...group,
          ...(this.dataGroupId ? {dataGroupId: this.dataGroupId} : {}),
          type: SelectorTypeDefinitions.CohortGroup,
          // guarantees the model always terminates groups correctly
          groupingOperator: index === groups.length - 1 ? null : group.groupingOperator ?? FiltersOperator.And,
          behavioralFiltersOperator: group.behavioralFiltersOperator ?? FiltersOperator.And,
        }))
      : [];
  }

  public toStagedCohort(): StagedCohort {
    /**
     * The top-down orientation of the builder limits the kind of selector trees that it can represent.
     * For instance, we cannot render an expression of (A or B) and C with the current UI. We can translate
     * this constraint to a tree representation that noting that any "and" operator must not have an "or"
     * operator as one of its children.
     */
    if (!onlyLeftAssociatedAndOperators(this.selector)) {
      throw new Error(`cannot render provided cohort selector tree`);
    }

    let literalTrees = getLiteralTrees(this.selector);
    let operators = getLogicalOperators(this.selector);
    if (!literalTrees.length) {
      if (!this.isReportBased()) {
        reporter.warn(`cohort expression tree not built by cohort builder`);
      }
      const selectorWithParents = this.selector.assignParents();
      literalTrees = getSingleClauseLiteralTrees(selectorWithParents);
      operators = getSingleClauseLogicalOperators(selectorWithParents);
    }

    const literals = literalTrees.map((tree, idx) => {
      const labelPrefix = LITERAL_NAMES[idx];
      // TODO: investigate moving this to the Literal parent
      if (isEventLiteralTree(tree)) {
        return EventLiteral.createFromParts({tree, labelPrefix, behaviors: this.behaviors});
      } else if (isCohortLiteralTree(tree)) {
        return CohortLiteral.createFromParts({tree, labelPrefix});
      }
      return PeopleLiteral.createFromParts({tree, labelPrefix});
    });

    return new StagedCohort({
      dataGroupId: this.dataGroupId,
      description: this.description,
      id: this.id,
      unsavedId: this.unsavedId,
      literals,
      name: this.name,
      operators,
      createdBy: this.createdBy,

      // permissions
      can_view: this.can_view,
      can_update_basic: this.can_update_basic,
      can_share: this.can_share,
      is_superadmin: this.is_superadmin,
      allow_staff_override: this.allow_staff_override,
    });
  }

  get creatorName() {
    if (this.createdBy.name) {
      return this.createdBy.name.trim();
    }
    if (this.createdBy.email) {
      return this.createdBy.email;
    }
    return ``;
  }

  public removeDependentCohortId(cohortId: number): Cohort {
    const dependentCohortIds = this.dependentCohortIds.filter((id) => id !== cohortId);
    return this.newCohort({dependentCohortIds});
  }

  public getBehaviors() {
    return Object.keys(this.behaviors ?? {}).reduce((acc, behaviorName) => {
      return {...acc, [behaviorName]: this.behaviors[behaviorName].serialize()};
    }, {});
  }

  public canExpandCohortDefinition(): boolean {
    // Note from Alix: Currently, all attirbution cohorts contain NthTimeInPeriodFilters.
    // I added the `isAttributionCohort` check here to future proof the code in case
    // other cohorts in the future that use attribution filters may support multiple
    // conversions.
    return !this.isReportBased() && !this.hasNthTimeInPeriodFilter() && !this.isAttributionCohort();
  }

  public isReportBased(): boolean {
    const hasInitializedGroups =
      Array.isArray(this.groups) && (this.groups.length > 1 || this.groups[0]?.filters?.length > 1);
    return hasInitializedGroups === false && Behavior.getReportName(this.getBehaviors()) !== null;
  }

  public isAttributionCohort(): boolean {
    const hasInitializedGroups = Array.isArray(this.groups) && this.groups.length > 0;
    if (!hasInitializedGroups) {
      return false;
    }
    return this.groups.some((group) =>
      group.filters?.some((groupFilter) => {
        const behaviorFilters = groupFilter.customProperty?.behavior?.filters;
        if (!behaviorFilters) {
          return false;
        }
        return behaviorFilters.some((behaviorFilter) => behaviorFilter.propertyName === `$multi_attribution_filter`);
      }),
    );
  }

  public hasNthTimeInPeriodFilter(): boolean {
    const hasInitializedGroups = Array.isArray(this.groups) && this.groups.length > 0;
    if (!hasInitializedGroups) {
      return false;
    }
    return this.groups.some((group) =>
      group.filters?.some((groupFilter) => {
        const behaviorFilters = groupFilter.customProperty?.behavior?.filters;
        if (!behaviorFilters) {
          return false;
        }
        return behaviorFilters.some(
          (behaviorFilter) =>
            behaviorFilter.filterOperator === NthTimeOperator.FirstTimeInPeriod ||
            behaviorFilter.filterOperator === NthTimeOperator.LastTimeInPeriod,
        );
      }),
    );
  }

  public setCount(count: number): Cohort {
    return this.newCohort({count, lastQueried: moment(new Date())});
  }

  public setDescription(description: string): Cohort {
    return this.newCohort({description, isNew: false, lastEdited: moment(new Date())});
  }

  public showNewIndicator(isNew = true): Cohort {
    return this.newCohort({isNew});
  }

  public isEqual(otherCohort: Cohort): boolean {
    return isEqual(this.serialize(), otherCohort.serialize());
  }

  public isValid(): boolean {
    return this.groups ? this.groups.length > 0 : !isEmpty(this.selector);
  }

  public includesSelfReference(): boolean {
    if (this.groups.length) {
      return this.groups.some((group) => group.filters?.some((filter) => cohortFilterIncludesId(filter, this.id)));
    }

    return this.toStagedCohort().includesReferenceToCohortId(this.id);
  }

  get hasGroups() {
    return Array.isArray(this.groups) && this.groups.length && !this.isReportBased();
  }

  public serialize(): SerializedCohort {
    return {
      description: this.description,
      name: this.name,
      id: this.id,
      unsavedId: this.unsavedId,

      /** @todo [pablo] figure out if we need to do 2-way transformations at this step */
      ...(this.hasGroups
        ? {groups: this.groups}
        : this.selector
          ? {
              selector: this.selector.serialize(),
              behaviors: this.getBehaviors(),
            }
          : {}),

      ...(this.dataGroupId ? {data_group_id: this.dataGroupId} : {}),

      // verification
      ...(this.verified ? {verified: this.verified} : {}),
      ...(this.verifiedBy ? {verified_by: this.verifiedBy} : {}),
      ...(this.lastVerified ? {last_verified: this.lastVerified.toISOString()} : {}),
    };
  }

  public toTrackingData(): Obj {
    const cohortTrackingData = {
      'cohort edited': this.lastEdited?.format(`YYYY-MM-DD`) ?? null,
      'cohort user count': this.count ?? null,
    };

    if (this.groups.length) {
      const numberOfLookupTablePropertyFilters = this.groups.reduce((count, group) => {
        return count + group.filters.filter(isLookupPropertyFilter).length;
      }, 0);

      /**
       * @todo [pablo] remove these aggregations once custom properties are able
       * to flatten results from a map() function, so that we can more dynamically
       * define metrics on based on the nested behaviors and filters in clauses
       */
      const groupData = {
        '# of user filters': 0,
        '# of behavioral filters': 0,
        '# of behaviors using 1st time filters': 0,
        '# of experiment filters': 0,
        'Behavioral aggregators': new Set<string>(),
        'Behavioral filters operators': new Set<string>(),
        'User filters operators': new Set<string>(),
      };

      for (const group of this.groups) {
        const [behavioralFrequencyFilters, filters] = partition(
          group.filters,
          (filter) => filter.customProperty && isBehavioralCustomProperty(filter.customProperty),
        );

        const behaviorTrackingData = behavioralFrequencyFilters.map((behavioralFilter) =>
          getBehaviorEntryTrackingProps(transformBehavioralFilterToBehavioralEntry(behavioralFilter)),
        );

        groupData[`# of user filters`] += filters.length;
        groupData[`# of behavioral filters`] += behavioralFrequencyFilters.length;
        groupData[`# of behaviors using 1st time filters`] += behaviorTrackingData.filter(
          (trackingData) => trackingData[`has 1st time filter`],
        ).length;
        groupData[`Behavioral filters operators`].add(group.behavioralFiltersOperator);
        groupData[`User filters operators`].add(group.behavioralFiltersOperator);
        groupData[`Behavioral aggregators`] = new Set<string>([
          ...groupData[`Behavioral aggregators`],
          ...behaviorTrackingData.map((trackingData) => trackingData[`Aggregator`]),
        ]);
        groupData[`# of experiment filters`] +=
          filters.filter((filter) => isExperimentSegment(filter)).length +
          behavioralFrequencyFilters.filter((filter) => isExperimentSegment(filter)).length;
      }

      return {
        ...cohortTrackingData,
        ...groupData,
        'Behavioral filters operators': Array.from(groupData[`Behavioral filters operators`]),
        'User filters operators': Array.from(groupData[`User filters operators`]),
        'Behavioral aggregators': Array.from(groupData[`Behavioral aggregators`]),
        // tracking data inherited from StagedCohort
        'all users literals': this.groups.length,
        [`and's`]: this.groups.filter((group) => group.groupingOperator === FiltersOperator.And).length,
        [`or's`]: this.groups.filter((group) => group.groupingOperator === FiltersOperator.Or).length,
        'cohort id': this.id,
        funnels: this.groups.filter((group) => group.behavioralFiltersOperator === FiltersOperator.Then).length,
        [commonTrackingEventPropertyName.dataGroupId]: this.dataGroupId,
        [commonTrackingEventPropertyName.numberOfLookupTablePropertyFilters]: numberOfLookupTablePropertyFilters,
        [commonTrackingEventPropertyName.usedLookupTableProperties]: numberOfLookupTablePropertyFilters > 0,
        [commonTrackingEventPropertyName.filterByGroupProperty]: this.groups.some((group) =>
          group.filters?.some((filter) => Boolean(filter.dataGroupId)),
        ),

        Clauses: this.groups.map((group) => {
          const [behavioralFrequencyFilters, filters] = partition(
            group.filters,
            (filter) => filter.customProperty && isBehavioralCustomProperty(filter.customProperty),
          );

          return {
            '# of user filters': filters.length,
            '# of behavioral filters': behavioralFrequencyFilters.length,
            'Behavioral filters operator': group.behavioralFiltersOperator,
            'User filters operator': group.filtersOperator,
            'User filters': filters.map((filter) => generateFilterTrackingProps(filter)),
            Behaviors: behavioralFrequencyFilters.map((behavioralFilter) =>
              getBehaviorEntryTrackingProps(transformBehavioralFilterToBehavioralEntry(behavioralFilter)),
            ),
          };
        }),
      };
    }

    const stagedCohort = this.toStagedCohort();
    return {...cohortTrackingData, ...stagedCohort.toTrackingData()};
  }

  public toPerformanceTrackingData(): Obj {
    const stagedCohort = this.toStagedCohort();
    const getDaysQueried = (behaviorsList: Behavior[]): number => {
      if (!behaviorsList || !behaviorsList.length) {
        return 0;
      }

      const dateRanges = behaviorsList.map((behavior) => {
        const {fromDate, toDate} = behavior.getDateRange();
        const fromDateStr = convertBaseDateToCalendarDate(fromDate);
        const toDateStr = convertBaseDateToCalendarDate(toDate);
        return {fromDate: parseDate(fromDateStr, {iso: true}), toDate: parseDate(toDateStr, {iso: true})};
      });
      const minFromDate = moment.utc(min(dateRanges.map((range) => range.fromDate)));
      const maxToDate = moment.utc(max(dateRanges.map((range) => range.toDate)));
      return maxToDate.diff(minFromDate, `days`);
    };

    const behaviors = Object.values(this.behaviors);
    const totalDaysQueried = getDaysQueried(behaviors);
    const emptySelectorDays = getDaysQueried(behaviors.filter((b) => b.producesEmptySelector()));
    const performanceTrackingData = {
      'total days queried': totalDaysQueried,
      'total days queried with empty selector': emptySelectorDays,
    };

    return {...performanceTrackingData, ...stagedCohort.toTrackingData()};
  }

  private newCohort(options: Partial<CohortOptions>): Cohort {
    const {
      activeIntegrations,
      behaviors,
      count,
      createdBy,
      dataGroupId,
      description,
      dependentCohortIds,
      id,
      isNew,
      lastEdited,
      reportName,
      name,
      selector,

      // verification
      verified,
      verifiedBy,
      lastVerified,

      /* eslint-disable @typescript-eslint/naming-convention */
      // permissions
      can_view,
      can_update_basic,
      can_share,
      is_superadmin,
      allow_staff_override,
      /* eslint-enable */
    } = this;
    return new Cohort({
      activeIntegrations,
      behaviors,
      count,
      createdBy,
      dataGroupId,
      description,
      dependentCohortIds,
      id,
      isNew,
      lastEdited,
      reportName,
      name,
      selector,

      // verification
      verified,
      verifiedBy,
      lastVerified,

      // permissions
      can_view,
      can_update_basic,
      can_share,
      is_superadmin,
      allow_staff_override,

      ...options,
    });
  }
}
